import { DeviceEventEmitter } from "react-native";
import Routes from "@navigations/Routes";
import { navigate } from "@navigations/utils/RootNavigation";
import { store } from "@redux/store";
import { getUserDetails } from "@scenes/authentication/utils/utils";

export type BannerLinkPlace = "web" | "app";

export type BannerLinkAction =
  | "openProfile"
  | "openTwoYearPlanPopup"
  | "openScreen";

export type BannerLink = {
  action: BannerLinkAction;
  text: string;
  screen?: string;
  params?: Record<string, string | number | boolean>;
  showOn?: BannerLinkPlace[];
};

export type BannerTextPart =
  | { kind: "text"; text: string }
  | { kind: "link"; link: BannerLink };

const LINK_TAG = /\{@link([^}]*)\}/g;
const LINK_ATTRIBUTE = /(\w+)="([^"]*)"/g;

const KNOWN_ACTIONS: BannerLinkAction[] = [
  "openProfile",
  "openTwoYearPlanPopup",
  "openScreen",
];

const SCREEN_NAMES: Record<string, string> = Object.keys(Routes).reduce(
  (names, key) => ({ ...names, [key]: Routes[key as keyof typeof Routes] }),
  {}
);

export const hasBannerLink = (message: string): boolean =>
  typeof message === "string" && message.includes("{@link");

const readParams = (value?: string): BannerLink["params"] => {
  if (!value) return undefined;
  const params: Record<string, string | number | boolean> = {};
  value.split(",").forEach((pair) => {
    const separator = pair.indexOf(":");
    if (separator === -1) return;
    const key = pair.slice(0, separator).trim();
    const rawValue = pair.slice(separator + 1).trim();
    if (!key) return;
    if (rawValue === "true" || rawValue === "false") {
      params[key] = rawValue === "true";
    } else if (rawValue !== "" && !isNaN(Number(rawValue))) {
      params[key] = Number(rawValue);
    } else {
      params[key] = rawValue;
    }
  });
  return Object.keys(params).length ? params : undefined;
};

const readLink = (tagContent: string): BannerLink => {
  const attributes: Record<string, string> = {};
  const reader = new RegExp(LINK_ATTRIBUTE.source, "g");
  let found = reader.exec(tagContent);
  while (found) {
    attributes[found[1]] = found[2];
    found = reader.exec(tagContent);
  }

  const action = KNOWN_ACTIONS.includes(attributes.action as BannerLinkAction)
    ? (attributes.action as BannerLinkAction)
    : "openProfile";

  const places = attributes.showOn
    ? (attributes.showOn
        .split(",")
        .map((place) => place.trim())
        .filter(
          (place) => place === "web" || place === "app"
        ) as BannerLinkPlace[])
    : undefined;

  return {
    action,
    text: attributes.text ?? "",
    screen: attributes.screen,
    params: readParams(attributes.params),
    showOn: places?.length ? places : undefined,
  };
};

const isLinkShownHere = (link: BannerLink, isWeb: boolean): boolean =>
  !link.showOn || link.showOn.includes(isWeb ? "web" : "app");

export const getBannerTextParts = (
  message: string,
  isWeb: boolean
): BannerTextPart[] => {
  const parts: BannerTextPart[] = [];
  const reader = new RegExp(LINK_TAG.source, "g");
  let readUpTo = 0;
  let found = reader.exec(message);

  while (found) {
    if (found.index > readUpTo) {
      parts.push({ kind: "text", text: message.slice(readUpTo, found.index) });
    }
    const link = readLink(found[1]);
    if (link.text && isLinkShownHere(link, isWeb)) {
      parts.push({ kind: "link", link });
    }
    readUpTo = found.index + found[0].length;
    found = reader.exec(message);
  }

  if (readUpTo < message.length) {
    parts.push({ kind: "text", text: message.slice(readUpTo) });
  }
  return parts;
};

const refreshUserDetails = () => {
  const { user, companyLocations } = store.getState().userInfo;
  if (!user?.id || !user?.token) return;
  getUserDetails(
    user.id,
    store.dispatch,
    companyLocations ?? [],
    user.token
  ).catch(() => undefined);
};

const openProfile = (isLargeScreen?: boolean) => {
  refreshUserDetails();
  if (isLargeScreen) {
    navigate(Routes.ExploreNavigator, {
      screen: Routes.AccountOverviewScreen,
    });
    return;
  }
  navigate(Routes.MoreNavigator, {
    screen: Routes.AccountOverviewUsersSeats,
    initial: false,
  });
};

export const openBannerLink = (
  link: BannerLink,
  { isLargeScreen }: { isLargeScreen?: boolean }
): void => {
  if (link.action === "openScreen") {
    const screenName = link.screen ? SCREEN_NAMES[link.screen] : undefined;
    if (!screenName) {
      openProfile(isLargeScreen);
      return;
    }
    navigate(isLargeScreen ? Routes.ExploreNavigator : Routes.MoreNavigator, {
      screen: screenName,
      ...(link.params ?? {}),
    });
    return;
  }

  openProfile(isLargeScreen);

  if (link.action === "openTwoYearPlanPopup") {
    setTimeout(
      () =>
        DeviceEventEmitter.emit("OPEN_MANAGE_PLAN_POPUP", {
          type: "renewal",
          plan_type: 2,
        }),
      200
    );
  }
};
